<template>
    <div>

        <Card :header="header" overlay>

            <template #header>
                <Tooltip v-if="tooltip" :text="tooltip" type="info">{{ $t(header) }}</Tooltip>
                <template v-else>{{ header }}</template>
            </template>

            <ErrorMessage :value="requestChart.getError()" />

            <template #nav>
                <div :key="spaceId" class="d-flex gap-3 flex-column">
                    <div class="d-flex gap-3 justify-content-end">
                        <b-dropdown :text="$t(`${currentChart}Filter`)" toggle-class="caret wide" :disabled="loading">
                            <template v-for="chartType in Object.keys(requests)">
                                <b-dropdown-item :key="chartType" :active="chartType === currentChart" @click="() => currentChart = chartType">{{ $t(`${chartType}Filter`) }}</b-dropdown-item>
                            </template>
                        </b-dropdown>
                        <MultiselectProducerProductCategories
                            v-show="showOnlyTopCategories"
                            :initial-value="categories"
                            :top="topCategories"
                            :disabled="loading"
                            :allow-empty="false"
                            @changeTrackBy="categories = $event"
                            @loading="categoriesLoading = $event"
                        />
                        <FormCategoryTreeSelect
                            v-show="!showOnlyTopCategories"
                            ref="FormCategoryTreeSelect"
                            :value="categories"
                            :disabled="loading"
                            class="width-200"
                            preselect
                            prevent-empty-selection
                            :flat="showResultsForParentCategories"
                            :clearable="false"
                            @change="categories = $event"
                            @loading="categoriesLoading = $event"
                        />
                        <MultiselectAvailableCampaigns
                            :disabled="loading"
                            @loading="campaignsLoading = $event"
                            @changeTrackBy="campaigns = $event"
                        />
                    </div>
                    <div class="d-flex gap-3 justify-content-end align-items-center">
                        <b-form-checkbox v-model="showOnlyTopCategories" switch :disabled="loading || showResultsForParentCategories">{{ $t('showOnlyTopCategories', { number: numberOfTopCategories }) }}</b-form-checkbox>
                        <b-form-checkbox v-model="showResultsForParentCategories" switch :disabled=" loading || showOnlyTopCategories">{{ $t('showResultsForParentCategories') }}</b-form-checkbox>
                    </div>
                </div>
            </template>

            <div class="d-flex gap-4 flex-column">
                <EChart
                    :options="chartOptions1"
                    :loading="loading"
                    type="time"
                    :header="$t('offersDisplay')"
                    card
                    no-padding
                    class="m-0 p-0 shadow-none"
                />
                <div class="d-flex gap-4 flex-wrap">
                    <EChart
                        :options="chartOptions2"
                        :loading="loading"
                        type="time"
                        :header="$t('percentageShare')"
                        card
                        no-padding
                        class="m-0 p-0 flex-basis-0 flex-grow-10 width-min-400 shadow-none"
                    />
                    <EChart
                        :options="chartOptions3"
                        :loading="loading"
                        :header="$t('percentageShareSum')"
                        card
                        no-padding
                        class="mb-0 width-min-500 flex-basis-0 flex-grow-1 shadow-none"
                    />
                </div>
            </div>

        </Card>
    </div>
</template>



<script>
    import { generateEChartData } from '@/utils/chart';
    import Tooltip from '@/components/common/Tooltip.vue';
    import EChart from '@/components/common/EChart.vue';
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import MultiselectProducerProductCategories from '@/components/common/MultiselectProducerProductCategories.vue';

    export default {
        name: 'ChartsReportMixin',
        components: {
            Tooltip,
            EChart,
            FormCategoryTreeSelect,
            MultiselectAvailableCampaigns,
            MultiselectProducerProductCategories,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                requests: {}, // Required
                currentChart: '', // Required
                requestProducerProductCategories: this.$req('space/ProducerProductCategories'),
                campaigns: [],
                categories: [],
                campaignsLoading: false,
                categoriesLoading: false,
                showOnlyTopCategories: true,
                showResultsForParentCategories: false,
                numberOfTopCategories: 10,
            };
        },
        computed: {
            header() {
                return this.$t('report'); // Required
            },
            tooltip() {
                return '';
            },
            loading() {
                return this.requestChart.getLoading() || this.loadingFilters;
            },
            loadingFilters() {
                return this.campaignsLoading || this.categoriesLoading;
            },
            loadingCategories() {
                return this.requestProducerProductCategories.getLoading();
            },
            categoriesList() {
                return this.requestProducerProductCategories.getData() || [];
            },
            requestChart() {
                return this.requests[this.currentChart] || null;
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange;
                const requestParams = { startDate, endDate };
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                return { urlParams, requestParams };
            },
            chartData() {
                if (this.loadingCategories) {
                    return {};
                }

                const apiData = this.requestChart.getData() || {};
                const result = {};
                const OTHERS_KEY = this.$t('others');

                // Zestawy do szybkiego lookup
                const selectedCategoryIds = new Set(this.categories);
                const topCategoryIds = new Set(this.topCategories);

                // Pomocnik do wyciągania ID z klucza "123 nazwa"
                const extractCategoryId = rawKey => Number(rawKey.split(' ')[0]);

                // --- Budujemy mapę kategorii oraz łańcuch przodków ---
                const categoryById = {};
                this.categoriesList.forEach(category => {
                    categoryById[category.id] = category;
                });

                const parentChains = {};
                this.categoriesList.forEach(category => {
                    const ancestorIds = [];
                    let parentCategory = category.parent;
                    while (parentCategory && parentCategory.id) {
                        ancestorIds.push(parentCategory.id);
                        parentCategory = parentCategory.parent;
                    }
                    parentChains[category.id] = ancestorIds;
                });

                // --- Pomocnik: jakie kategorie dostaną wartość z liścia categoryId?
                const collectContributionCategoryIds = categoryId => {
                    const buckets = [];
                    // sama kategoria, jeśli wybrana
                    if (selectedCategoryIds.has(categoryId)) {
                        buckets.push(categoryId);
                    }
                    // dla nadrzędnych, jeśli flaga włączona
                    if (this.showResultsForParentCategories) {
                        parentChains[categoryId].forEach(parentId => {
                            if (selectedCategoryIds.has(parentId)) {
                                buckets.push(parentId);
                            }
                        });
                    }
                    return buckets;
                };

                // ----- 1) Time-series (wykres liniowy) -----
                if (Array.isArray(apiData.chart) && apiData.chart.length) {
                    result.chart = apiData.chart.map(rawChartEntry => {
                        const chartEntry = { date: rawChartEntry.date };
                        let othersSum = 0;

                        Object.entries(rawChartEntry).forEach(([rawKey, metricValue]) => {
                            if (rawKey === 'date') return;
                            const categoryId = extractCategoryId(rawKey);

                            // jeśli sumujemy dla rodziców
                            if (this.showResultsForParentCategories) {
                                const contributionCategoryIds = collectContributionCategoryIds(categoryId);
                                if (contributionCategoryIds.length === 0) return;
                                contributionCategoryIds.forEach(targetId => {
                                    const category = categoryById[targetId];
                                    const entryKey = `${category.id} ${category.name}`;
                                    chartEntry[entryKey] = (chartEntry[entryKey] || 0) + metricValue;
                                });
                                return;
                            }

                            // w przeciwnym razie standard top/others/liście
                            if (this.showOnlyTopCategories && !topCategoryIds.has(categoryId)) {
                                othersSum += metricValue;
                            } else if (selectedCategoryIds.has(categoryId)) {
                                // zachowujemy oryginalny rawKey, bo to klucz "id nazwa"
                                chartEntry[rawKey] = metricValue;
                            }
                        });

                        if (this.showOnlyTopCategories) {
                            chartEntry[OTHERS_KEY] = othersSum;
                        }
                        return chartEntry;
                    });
                }

                // ----- 2) Pie-chart (wykres kołowy) -----
                if (Array.isArray(apiData.pie_chart) && apiData.pie_chart.length) {
                    const pieValueMap = {};

                    apiData.pie_chart.forEach(([rawKey, metricValue]) => {
                        const categoryId = extractCategoryId(rawKey);

                        if (this.showResultsForParentCategories) {
                            const contributionCategoryIds = collectContributionCategoryIds(categoryId);
                            if (contributionCategoryIds.length === 0) return;
                            contributionCategoryIds.forEach(targetId => {
                                const entryKey = `${categoryById[targetId].id} ${categoryById[targetId].name}`;
                                pieValueMap[entryKey] = (pieValueMap[entryKey] || 0) + metricValue;
                            });
                        } else if (selectedCategoryIds.has(categoryId)) {
                            // oryginalny rawKey jako klucz
                            pieValueMap[rawKey] = (pieValueMap[rawKey] || 0) + metricValue;
                        }
                    });

                    let pieArray = Object.entries(pieValueMap);
                    if (this.showOnlyTopCategories) {
                        const topItems = [];
                        let othersSum = 0;
                        pieArray.forEach(([key, value]) => {
                            const categoryId = extractCategoryId(key);
                            if (topCategoryIds.has(categoryId)) {
                                topItems.push([key, value]);
                            } else {
                                othersSum += value;
                            }
                        });
                        topItems.push([OTHERS_KEY, othersSum]);
                        pieArray = topItems;
                    }

                    result.pie_chart = pieArray;
                }

                return result;
            },
            chartSeries() {
                const chartData = (this.chartData?.chart || []).reduce((acc, item) => {
                    Object.keys(item).forEach(name => {
                        if (name !== 'date') {
                            if (!acc[name]) acc[name] = []; // eslint-disable-line no-param-reassign
                            acc[name].push([item.date, item[name]]);
                        }
                    });
                    return acc;
                }, {});
                return Object.keys(chartData).map(name => ({
                    name,
                    data: generateEChartData(this.timeRange, chartData[name], this.groupBy),
                }));
            },
            chartOptions1() {
                if (!this.chartSeries.length) {
                    return { series: [] };
                }
                const names = this.chartSeries.map(item => item.name);
                const series = this.chartSeries.map(item => ({
                    ...item,
                    type: 'line',
                }));
                return {
                    legend: {
                        data: names,
                    },
                    yAxis: {
                        type: 'value',
                        name: this.$t(`${this.currentChart}`),
                    },
                    series,
                };
            },
            chartOptions2() {
                if (!this.chartSeries.length) {
                    return { series: [] };
                }
                const pivotSeries = (series, filterZeros = false) => {
                    /* eslint-disable no-param-reassign */
                    // 1) Zbierz wszystkie rekordy w Map<timestamp, record>
                    const dateMap = new Map();
                    for (const { name, data } of series) {
                        for (const [ts, val] of data) {
                            const rec = dateMap.get(ts) ?? { date: ts };
                            rec[name] = val;
                            dateMap.set(ts, rec);
                        }
                    }
                    // 2) Upewnij się, że każdy record ma wszystkie nazwy (domyślnie 0)
                    const names = series.map(s => s.name);
                    const pivoted = [...dateMap.values()].map(rec => {
                        for (const name of names) {
                            rec[name] = rec[name] ?? 0;
                        }
                        return rec;
                    });
                    // 3) (opcjonalnie) odfiltruj rekordy z samymi zerami
                    return filterZeros ? pivoted.filter(rec => names.some(name => rec[name] !== 0)) : pivoted;
                    /* eslint-enable no-param-reassign */
                };
                const rawData = pivotSeries(this.chartSeries, false);
                const names = this.chartSeries.map(item => item.name);
                return {
                    legend: {
                        data: names,
                    },
                    dataset: [
                        { source: rawData },
                        {
                            transform: {
                                type: 'percent',
                                config: {
                                    dimension: names,
                                },
                            },
                        },
                    ],
                    series: names.map(name => ({
                        name,
                        type: 'bar',
                        stack: 'total',
                        datasetIndex: 1,
                        encode: {
                            x: 'date',
                            y: name,
                        },
                    })),
                };
            },
            chartOptions3() {
                const chartData = this.chartData.pie_chart;
                if (!chartData) {
                    return { series: [] };
                }
                return {
                    series: [
                        {
                            type: 'pie',
                            data: chartData.map(([name, value]) => ({ name, value })),
                        },
                    ],
                };
            },
            topCategories() {
                const data = this.requestChart.getData() || {};
                return [...(data.pie_chart || [])]
                    .sort((a, b) => b[1] > a[1])
                    .slice(0, this.numberOfTopCategories)
                    .map(item => Number.parseInt(item[0].split(' ')[0]));
            },
        },
        watch: {
            async spaceId() {
                this.resetFilters();
                await this.fetchProducerProductCategories();
            },
            apiParams: {
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        this.loadData();
                    }
                },
            },
            currentChart: {
                handler: 'loadData',
            },
        },
        methods: {
            loadData() {
                if (!this.loadingFilters) {
                    this.resetData();
                    this.fetchData();
                }
            },
            resetData() {
                this.requestChart.abortAll();
                this.requestChart.resetAll();
            },
            fetchData() {
                this.requestChart.get(this.apiParams);
            },
            resetFilters() {
                this.campaigns = [];
                this.categories = [];
                this.campaignsLoading = false;
                this.categoriesLoading = false;
                this.showOnlyTopCategories = true;
                this.showResultsForParentCategories = false;
            },
            fetchProducerProductCategories() {
                return this.requestProducerProductCategories.get(this.spaceId);
            },
        },
    };
</script>



<i18n lang="json" locale="pl" scope="local">
{
    "amountFilter": "Suma sprzedaży",
    "quantityFilter": "Liczba [sztuk]",
    "impressionsFilter": "Wyświetlenia ofert",
    "clicksFilter": "Kliknięcia"
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "amountFilter": "Sum of sales",
    "quantityFilter": "Number [pieces]",
    "impressionsFilter": "Offers display",
    "clicksFilter": "Clicks"
}
</i18n>

